import type { CalendarEntry } from './api';

export type CalendarView = 'month' | 'week' | 'day' | 'agenda';
export const ZONE = 'Asia/Dubai';
export function dateKey(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  return ['year', 'month', 'day'].map((type) => parts.find((part) => part.type === type)?.value).join('-');
}
export function addDays(key: string, days: number): string {
  const date = new Date(`${key}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
export function calendarDays(key: string, view: CalendarView): string[] {
  const date = new Date(`${key}T12:00:00Z`);
  if (view === 'month') date.setUTCDate(1);
  let first = date.toISOString().slice(0, 10);
  if (view === 'month' || view === 'week') first = addDays(first, -((date.getUTCDay() + 6) % 7));
  const last = new Date(date);
  last.setUTCMonth(last.getUTCMonth() + 1, 0);
  const monthCells = Math.ceil((((date.getUTCDay() + 6) % 7) + last.getUTCDate()) / 7) * 7;
  return Array.from({ length: view === 'month' ? monthCells : view === 'day' ? 1 : 7 }, (_, index) => addDays(first, index));
}
export function movePeriod(key: string, view: CalendarView, direction: number): string {
  if (view !== 'month') return addDays(key, direction * (view === 'day' ? 1 : 7));
  const date = new Date(`${key}T12:00:00Z`);
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + direction);
  return date.toISOString().slice(0, 10);
}
export function onDay(event: CalendarEntry, key: string): boolean {
  if (!event.startsAt) return false;
  const start = dateKey(new Date(event.startsAt));
  if (event.kind === 'DAY' || event.kind === 'TIMED') return start === key;
  const end = dateKey(new Date(event.endsAt ? new Date(event.endsAt).getTime() - 1 : new Date(event.startsAt).getTime()));
  return start <= key && key <= end;
}
export function clockMinutes(iso: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: ZONE, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(iso));
  return Number(parts.find((part) => part.type === 'hour')?.value) * 60 + Number(parts.find((part) => part.type === 'minute')?.value);
}
// Interval groups share available horizontal space; overlapping events never cover each other.
export function eventLanes(events: CalendarEntry[]) {
  const sorted = events.filter((event) => event.startsAt).map((event) => {
    const start = clockMinutes(event.startsAt!);
    const end = event.endsAt && dateKey(new Date(event.endsAt)) === dateKey(new Date(event.startsAt!)) ? clockMinutes(event.endsAt) : start + 60;
    return { event, start, end: Math.min(1440, Math.max(start + 30, end)), lane: 0, lanes: 1 };
  }).sort((a, b) => a.start - b.start);
  let group: typeof sorted = [];
  let ends: number[] = [];
  const flush = () => { for (const item of group) item.lanes = ends.length; group = []; ends = []; };
  for (const item of sorted) {
    if (group.length && item.start >= Math.max(...ends)) flush();
    const free = ends.findIndex((end) => end <= item.start);
    item.lane = free < 0 ? ends.length : free;
    ends[item.lane] = item.end;
    group.push(item);
  }
  flush();
  return sorted;
}
